import utils from "./utils.implant.mjs"

const rcee = {}

const parsePower = (value) => parseFloat((value || '').toString().replace(',', '.')) || 0
const formatAmount = (value) => value > 0 ? value.toFixed(2).replace('.', ',') : ''

const filterGenerators = (generators = [], code) => generators.filter(gen => {
  if (gen.archived) {
    return false
  }

  if (code) {
    return gen.code === code
  }

  return !gen.code
})

rcee.getImplantTypes = (implant, code) => {
  if (implant) {
    const generators = utils.getGenerators(implant).filter(gen => ((code && gen.code === code) || (!code && !gen.code)))

    //var to check if all the generators are complete
    let completed = 0

    let powerThermicSolid = 0
    let powerThermicGas = 0
    let powerPumpElectric = 0
    let powerPumpEngine = 0
    let powerPumpThermic = 0

    generators.forEach(gen => {
      //se è un gruppo termico o bruciatore
      if (gen.power_useful) {
        if (['Combustibile solido'].includes(gen.fuel)) {
          completed++
          powerThermicSolid += parseFloat(gen.power_useful)
        } else if (['Metano', 'Gpl', 'Butano', 'Biogas', 'Propano'].includes(gen.fuel)) {
          completed++
          powerThermicGas += parseFloat(gen.power_useful)
        }
      }

      //se è una pompa di calore o gruppo frigo
      if (gen.drive_system) {
        let power = 0
        if (gen.power_nominal_cool) {
          power += parseFloat(gen.power_nominal_cool)
        } else if (gen.power_nominal_hot) {
          power += parseFloat(gen.power_nominal_hot)
        }

        if (['A ciclo di compressione con motore elettrico', 'Ad assorbimento a fiamme dirette'].includes(gen.drive_system)) {
          completed++
          powerPumpElectric += power
        } else if (['A ciclo di compressione con motore endotermico'].includes(gen.drive_system)) {
          completed++
          powerPumpEngine += power
        } else if (['Ad assorbimento per recupero di calore'].includes(gen.drive_system)) {
          completed++
          powerPumpThermic += power
        }
      }
    })

    return {
      incomplete: (generators.length - completed),
      
      powerThermicSolid,
      powerThermicGas,
      powerPumpElectric,
      powerPumpEngine,
      powerPumpThermic
    }
  } else {
    return null
  }
}

rcee.calcRcee = (implant, code) => {
  const powers = rcee.getImplantTypes(implant, code)

  if (powers) {
    let foundSingle = false
    let error = false
    let years = false
    for (const [key, value] of Object.entries(powers)) {
      //check if there are multiple types of power supply
      if (value > 0) {
        if (foundSingle) {
          error = true
        }

        foundSingle = true
      }

      //check on power value to calc years
      if (key === 'powerThermicSolid') {
        if (value >= 10 && value <= 100) {
          years = 2
        } else if (value > 100) {
          years = 1
        }
      } else if (key === 'powerThermicGas') {
        if (value >= 10 && value <= 100) {
          years = 4
        } else if (value > 100) {
          years = 2
        }
      } else if (key === 'powerPumpElectric') {
        if (value >= 12 && value <= 100) {
          years = 4
        } else if (value > 100) {
          years = 2
        }
      } else if (key === 'powerPumpEngine') {
        if (value >= 12) {
          years = 4
        }
      } else if (key === 'powerPumpThermic') {
        if (value >= 12) {
          years = 2
        }
      }
    }
    
    return {
      years,
      error,
      incomplete: powers.incomplete
    }
  }
}

rcee.calcNextRcee = (implant, revision) => {
  const response = rcee.calcRcee(implant, revision.code)
  if (!response.error && response.years && revision?.log?.length) {
    const rceeLogs = revision.log.toSorted((a, b) => new Date(b.date) - new Date(a.date)).filter(log => log.rcee)
    if (rceeLogs?.length) {
      const latest = new Date(rceeLogs[0].date)
      const latestYear = latest.getFullYear()

      const nextRcee = latestYear + response.years
      return nextRcee
    }
  }
}

rcee.calcBollino = (implant, code) => {
  if (!implant) {
    return ''
  }

  const thermals = filterGenerators(implant.generator_thermals, code)
  const burners = filterGenerators(implant.generator_burners, code)
  const pumps = filterGenerators(implant.generator_pumps, code)
  const exchangers = filterGenerators(implant.generator_exchangers, code)
  const cogenerators = filterGenerators(implant.generator_cogenerators, code)

  const thermalPower = thermals.concat(burners).reduce((sum, gen) => sum + parsePower(gen.power_useful), 0)
  const pumpPower = pumps.reduce((sum, gen) => {
    const powerCool = parsePower(gen.power_nominal_cool)
    const powerHot = parsePower(gen.power_nominal_hot)

    return sum + (powerCool || powerHot)
  }, 0)

  const hasThermal = thermalPower > 0
  const hasPump = pumpPower > 0
  const hasDistrictHeating = exchangers.length > 0
  const hasCogenerative = cogenerators.length > 0

  const typesFound = [hasThermal, hasPump, hasDistrictHeating, hasCogenerative].filter(Boolean).length
  if (typesFound !== 1) {
    return ''
  }

  if (hasThermal) {
    if (thermalPower <= 100) {
      return formatAmount(14)
    }
    if (thermalPower <= 200) {
      return formatAmount(56)
    }
    if (thermalPower <= 300) {
      return formatAmount(98)
    }

    return formatAmount(140)
  }

  if (hasPump) {
    return formatAmount(pumpPower <= 100 ? 14 : 56)
  }

  if (hasDistrictHeating) {
    return formatAmount(14)
  }

  if (hasCogenerative) {
    return formatAmount(56)
  }

  return ''
}

export default rcee